import { useEffect, useMemo, useState, useCallback } from "react";
import {
  BroadcastManyQuery,
  EnumBroadcastResourcesType,
  useOrdersForBroadcastResourceLazyQuery,
  useSurveyResponseForBroadcastResourceLazyQuery,
  useSurveyResponsesForBroadcastResourceLazyQuery,
} from "gql/generated";
import { BroadcastStatus } from "apps/home/constants";
import { isSurveyDesignFieldType } from "shared/components/organisms/SurveyQuestionInput/surveyQuestionDesign";
import _ from "lodash";

type Resource = NonNullable<
  BroadcastManyQuery["broadcastMany"][0]["resources"][0]
>;

export type FormResourceInfo = {
  status: BroadcastStatus;
  formProgress: { answered: number; total: number } | null;
  allowSaveAndEdit: boolean;
  submissionCount?: number;
  submittedCount?: number;
  pendingCount?: number;
  draftCount?: number;
  rejectedCount?: number;
  bookedCount?: number;
};

function mapSurveyResponseStatus(
  responseStatus: string | null | undefined,
): BroadcastStatus {
  switch (responseStatus) {
    case "DRAFT":
      return "FORM_DRAFT";
    case "SUBMITTED":
      return "FORM_SUBMITTED";
    case "PENDING_REVIEW":
      return "FORM_PENDING_REVIEW";
    case "REJECTED":
      return "FORM_REJECTED";
    default:
      return "NOT_VIEWED";
  }
}

function computeProgress(
  responses:
    | Array<{
        response?: (string | null)[] | null;
        questionData?: { type?: string | null } | null;
      } | null>
    | null
    | undefined,
  totalQuestions: number,
): { answered: number; total: number } | null {
  if (!responses?.length || totalQuestions === 0) return null;
  const answered = responses.filter(r => {
    if (isSurveyDesignFieldType(r?.questionData?.type)) return false;
    const vals = _.compact(r?.response);
    return vals.length > 0 && vals.some(v => v !== "" && v !== "false");
  }).length;
  return { answered, total: totalQuestions };
}

export function useFormResourceStatuses(
  resources: Resource[],
  broadcastId: string | undefined,
  personId: string | undefined,
  isPreview: boolean,
) {
  const [statusMap, setStatusMap] = useState<Record<string, FormResourceInfo>>(
    {},
  );

  const [loadExisting] = useSurveyResponseForBroadcastResourceLazyQuery();
  const [loadMultiple] = useSurveyResponsesForBroadcastResourceLazyQuery();
  const [loadOrders] = useOrdersForBroadcastResourceLazyQuery();

  const formResources = useMemo(
    () => resources.filter(r => r.type === EnumBroadcastResourcesType.Forms),
    [resources],
  );

  const storefrontResources = useMemo(
    () => resources.filter(r => r.type === EnumBroadcastResourcesType.Storefront),
    [resources],
  );

  const fetchStatuses = useCallback(async () => {
    if (!broadcastId || !personId || isPreview) return;
    if (formResources.length === 0 && storefrontResources.length === 0) return;

    const formResults = await Promise.all(
      formResources.map(async r => {
        const formMode = r.formMode ?? "ONE_AND_DONE";
        const isShared = formMode === "COLLABORATIVE";
        const totalQuestions =
          r.survey?.questions?.filter(
            q => q?.isActive && !isSurveyDesignFieldType(q?.type),
          ).length ?? 0;

        try {
          if (formMode === "MULTI_SUBMISSION") {
            const result = await loadMultiple({
              variables: { broadcastId, resourceId: r._id, personId },
            });
            const subs = result.data?.surveyResponsesForBroadcastResource ?? [];
            const submittedCount = subs.filter(s => s?.status === "SUBMITTED").length;
            const pendingCount = subs.filter(s => s?.status === "PENDING_REVIEW").length;
            const rejectedCount = subs.filter(s => s?.status === "REJECTED").length;
            const draftCount = subs.filter(s => s?.status === "DRAFT").length;
            let status: BroadcastStatus = "NOT_VIEWED";
            if (pendingCount > 0) status = "FORM_PENDING_REVIEW";
            else if (rejectedCount > 0) status = "FORM_REJECTED";
            else if (draftCount > 0) status = "FORM_DRAFT";
            else if (submittedCount > 0) status = "FORM_SUBMITTED";

            return {
              resourceId: r._id,
              info: {
                status,
                formProgress: null,
                allowSaveAndEdit: r.allowSaveAndEdit ?? false,
                submissionCount: subs.length,
                submittedCount,
                pendingCount,
                draftCount,
                rejectedCount,
              } as FormResourceInfo,
            };
          }

          const result = await loadExisting({
            variables: {
              broadcastId,
              resourceId: r._id,
              personId,
              isShared: isShared || undefined,
            },
          });
          const sr = result.data?.surveyResponseForBroadcastResource;
          return {
            resourceId: r._id,
            info: {
              status: mapSurveyResponseStatus(sr?.status),
              formProgress: sr
                ? computeProgress(sr.responses, totalQuestions)
                : null,
              allowSaveAndEdit: isShared || (r.allowSaveAndEdit ?? false),
            } as FormResourceInfo,
          };
        } catch {
          return {
            resourceId: r._id,
            info: {
              status: "NOT_VIEWED" as BroadcastStatus,
              formProgress: null,
              allowSaveAndEdit: r.allowSaveAndEdit ?? false,
            },
          };
        }
      }),
    );

    const storefrontResults = await Promise.all(
      storefrontResources.map(async r => {
        try {
          const result = await loadOrders({
            variables: {
              broadcastResourceId: r._id,
              personId,
            },
          });
          const orders = result.data?.ordersForBroadcastResource ?? [];
          const confirmedCount = orders.filter(o => o && !o.cancelled).length;
          return {
            resourceId: r._id,
            info: {
              status: (confirmedCount > 0 ? "COMPLETED" : "NOT_VIEWED") as BroadcastStatus,
              formProgress: null,
              allowSaveAndEdit: false,
              bookedCount: confirmedCount,
            } as FormResourceInfo,
          };
        } catch {
          return {
            resourceId: r._id,
            info: {
              status: "NOT_VIEWED" as BroadcastStatus,
              formProgress: null,
              allowSaveAndEdit: false,
            },
          };
        }
      }),
    );

    const map: Record<string, FormResourceInfo> = {};
    for (const { resourceId, info } of [...formResults, ...storefrontResults]) {
      map[resourceId] = info;
    }
    setStatusMap(map);
  }, [broadcastId, personId, isPreview, formResources, storefrontResources, loadExisting, loadMultiple, loadOrders]);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  return { formStatusMap: statusMap, refetchFormStatuses: fetchStatuses };
}
